import {
	FrequentlyAskedQuestionItemSaveModel
} from "client/Api/Hub/common.composer";
import {
	AutogrowTextArea,
	AutogrowTextAreaHandle,
	Button,
	Portal,
	RewriteToolsMenu,
	RewriteToolsResult,
	RichTextEditorContent,
	RichTextEditorContentHandle,
	RichTextEditorIntermediateStateHandlers,
	RichTextEditorRewriteToolsResult,
	RichTextEditorToolbar,
	usePlainTextRewriteTools,
	useRichTextEditorIntermediateState,
	useRichTextEditorRewriteTools,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	IResolveAttachableRichTextEditorPropsWithBuilder
} from "client/Forms/attachableProps/richTextEditor";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useBuilderEditor } from "../../useBuilderEditor";
import {
	getClientId,
	hasClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import styles from "./FrequentlyAskedQuestions.module.less";
import { InlineActionList } from "../../Actions/InlineActionList";
import classNames from "classnames";
import {
	ensureNotNil,
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { clearFocus } from "client/Misc/domUtils";
import { InputValidationResultKind } from "client/Validation";
import { FrequentlyAskedQuestionsItemLayout } from "client/Features/Hub/FrequentlyAskedQuestionsItemLayout";
import { userMentionType } from "client/Components/MentionInput/converters";
import { DropOverMentionSuggestionBox } from "client/Features/Hub/MentionSuggestionBox/DropOverMentionSuggestionBox";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { findUsers } from "client/Api/Hub/hub";
import { generateUserDetailsUrl } from "../../misc";
import { FrequentlyAskedQuestionElementFormModel } from "client/Features/Hub/Management/common";
import { ElementLayout } from "../ElementLayout";
import { ElementGenericActionHandlers, ElementGenericHandle } from "../Element";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useDelayedEvent, useIsTouchDevice } from "client/Hooks";
import { RewriteToolEditorKind, useRewriteToolsService } from "client/Features/ChatAssistant/useRewriteToolsService";
import { hubItemKindToRewriteToolTargetKindMap } from "client/Features/Hub/common";
import { Slice } from "node_modules/prosemirror-model/dist";
import { EditorState } from "node_modules/prosemirror-state/dist";
import {
	RewriteToolHandlers,
	RewriteToolOriginalSliceInfo,
	RewriteToolResponse,
	SuccessfulRewriteToolResponse
} from "client/Components/RewriteToolsMenu/common";
import { RichTextEditorRewriteToolHandlers } from "client/Components/RichTextEditor/tools/useRichTextEditorRewriteTools";
import { RewriteToneKind, TranslateLangKind } from "client/Api/AiAssistant/aiAssistant";
import { useAiAssistantConfiguration } from "client/Features/ChatAssistant/useAiAssistantConfiguration";

export type FrequentlyAskedQuestionsProps = {
	attachable: IValueInputAttachable<FrequentlyAskedQuestionElementFormModel>;
	onDeleteElement: () => void;
	genericHandlers: ElementGenericActionHandlers;
	ref: Ref<ElementGenericHandle>;
	hubItemKind: HubItemKind;
};

export const FrequentlyAskedQuestions = (props: FrequentlyAskedQuestionsProps): ReactElement => {
	const {
		attachable,
		onDeleteElement,
		genericHandlers,
		ref,
		hubItemKind
	} = props;

	const [itemToScrollTo, setItemToScrollTo] = useState<Guid | null>(null);

	const { value } = attachable;

	const {
		onAddNewElementToColumn,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage,
		onMoveUp,
		onMoveDown,
		onDuplicate,
		onDelete
	} = genericHandlers;

	const itemsRef: ReadonlyRefObject<Map<Guid, (ItemHandle | null)>> = useRef<Map<Guid, (ItemHandle | null)>>(new Map());
	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const setRef = useCallback(
		(itemId: Guid, itemHandle: ItemHandle | null): void => {
			if (isNil(itemHandle)) {
				itemsRef.current
					.delete(itemId);
			} else {
				itemsRef
					.current
					.set(
						itemId,
						itemHandle
					);
			}
		},
		[]
	);

	const moveItemHandler = useCallback(
		(fromIndex: number, toIndex: number ): void => {
			const itemsCopy: ObjectWithClientId<FrequentlyAskedQuestionItemSaveModel>[] = [...attachable
				.for("items")
				.value
			];

			if (toIndex > itemsCopy.length - 1
				|| toIndex < 0
			) {
				return;
			}

			const itemToMove: ObjectWithClientId<FrequentlyAskedQuestionItemSaveModel> = itemsCopy
				.splice(fromIndex, 1)[0];

			itemsCopy
				.splice(
					toIndex,
					0,
					itemToMove
				);

			attachable.for("items")
				.reducer
				.setValue(itemsCopy);

			attachable.for("items")
				.validation
				.triggerInputFinished();

			setItemToScrollTo(getClientId(itemToMove));
		},
		[attachable]
	);

	const deleteItemHandler = useCallback(
		(index: number): void => {
			if (attachable.for("items")
				.value
				.length <= 1
			) {
				onDeleteElement();
				return;
			}

			attachable.for("items")
				.reducer
				.removeAt(index);

			attachable.for("items")
				.validation
				.triggerInputFinished();

			clearFocus();
		},
		[
			attachable,
			onDeleteElement
		]
	);

	const addItemHandler = useCallback(
		(insertAfterIndex?: number): void => {
			const targetIndex: number = isNotNil(insertAfterIndex)
				? insertAfterIndex + 1
				: attachable.for("items")
					.value
					.length;

			const newItem: ObjectWithClientId<FrequentlyAskedQuestionItemSaveModel> = setClientId({
				question: "",
				answer: ""
			});

			attachable.for("items")
				.reducer
				.insertAt(
					targetIndex,
					newItem
				);

			attachable.for("items")
				.validation
				.triggerInputFinished();

			clearFocus();

			const clientId: Guid = getClientId(newItem);

			setTimeout(
				(): void => {
					itemsRef.current
						.get(clientId)
						?.focusQuestion();
				},
				0
			);
		},
		[attachable]
	);

	const focusEntireContainerHandler = (): void => {
		ensureNotNil(rootRef.current)
			.focus();
	};

	useImperativeHandle(
		ref,
		(): ElementGenericHandle => ({
			findAndFocus: (clientId: Guid): void => {
				if (hasClientId(value) && getClientId(value) !== clientId) {
					return;
				}

				if (value.items.length > 0) {
					const itemId: Guid = getClientId(value.items[0]);

					itemsRef.current
						.get(itemId)
						?.focusQuestion();
				}
			},
			scrollIntoView: (): void => {
				ensureNotNil(rootRef.current)
					.scrollIntoView();
			}
		}),
		[value]
	);

	useLayoutEffect(
		(): void => {
			if (itemToScrollTo) {
				itemsRef.current
					.get(itemToScrollTo)
					?.scrollIntoView();

				setItemToScrollTo(null);
			}
		},
		[itemToScrollTo]
	);

	return (
		<ElementLayout
			trackingId={attachable.reducer.path}
			// Is handled inside the element
			// for each item of it
			hasError={false}
			ref={rootRef}
		>
			<ElementLayout.ActionsMenu trackingId="element-actions">
				<ElementLayout.AddElementMenuItem
					onAddNewElementToColumn={onAddNewElementToColumn}
					onOpenEmbedInputDialog={onOpenEmbedInputDialog}
					onOpenVideoInputDialog={onOpenVideoInputDialog}
					onSelectImage={onSelectImage}
					trackingId={generateTrackingId(attachable.reducer.path, "__add-element-menu")}
				/>
				{onMoveUp && (
					<ElementLayout.MoveUpMenuItem
						onClick={onMoveUp}
					/>
				)}
				{onMoveDown && (
					<ElementLayout.MoveDownMenuItem
						onClick={onMoveDown}
					/>
				)}
				<ElementLayout.DuplicateMenuItem
					onClick={onDuplicate}
				/>
				<ElementLayout.DeleteMenuItem
					onClick={onDelete}
				/>
				<InlineActionList.Divider />
				<Button
					kind="transparent-for-list"
					icon="plus"
					text={localize("_AddQuestion")}
					onClick={() => addItemHandler()}
					trackingId="add-question"
				/>
			</ElementLayout.ActionsMenu>
			<ul
				className={styles.root}
			>
				{attachable
					.for("items")
					.map(
						(item, index) => {
							const clientId: Guid = getClientId(item.value);

							return (
								<Item
									key={clientId}
									ref={r => setRef(clientId, r)}
									attachable={item}
									hubItemKind={hubItemKind}
									onSelectEntireFAQ={value.items.length > 1
										? focusEntireContainerHandler
										: undefined
									}
									onAdd={() => addItemHandler(index)}
									onDelete={() => deleteItemHandler(index)}
									onMoveDown={index < value.items.length - 1
										? () => moveItemHandler(index, index + 1)
										: undefined
									}
									onMoveUp={index > 0
										? () => moveItemHandler(index, index - 1)
										: undefined
									}
								/>
							);
						}
					)}
			</ul>
		</ElementLayout>
	);
};
FrequentlyAskedQuestions.displayName = "FrequentlyAskedQuestions";

type ItemHandle = {
	focusQuestion: () => void;
	scrollIntoView: () => void;
};

type ActiveField = "question" | "answer";

type ItemProps = {
	attachable: IValueInputAttachable<FrequentlyAskedQuestionItemSaveModel>;
	hubItemKind: HubItemKind;
	onDelete: () => void;
	onAdd: () => void;
	onSelectEntireFAQ?: () => void;
	onMoveUp?: () => void;
	onMoveDown?: () => void;
};

const Item = forwardRef(
	(props: ItemProps, ref: Ref<ItemHandle>): ReactElement => {
		const {
			attachable,
			hubItemKind,
			onMoveDown,
			onMoveUp,
			onDelete,
			onAdd,
			onSelectEntireFAQ
		} = props;

		const [isActive, setIsActive] = useState<boolean>(false);
		const [activeField, setActiveField] = useState<ActiveField | null>(null);
		const [isImproveTextMenuOpen, setIsImproveTextMenuOpen] = useState<boolean>(false);
		const [questionSelectionRange, setQuestionSelectionRange] = useState<[number, number]>([0, 0]);

		const { isAssistMeAvailable } = useAiAssistantConfiguration();
		const isTouchDevice: boolean = useIsTouchDevice();

		const itemRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
		const questionRef: RefObject<QuestionHandle | null> = useRef<QuestionHandle>(null);
		const answerRef: RefObject<AnswerHandle | null> = useRef<AnswerHandle>(null);
		const improveTextButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

		const questionHandler : IValueInputAttachable<string> = attachable.for("question");
		const answerHandler : IValueInputAttachable<string> = attachable.for("answer");

		const answerIntermediateState: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState();

		// NOTE: with the current infrastructure the attachToRichTextEditor(Toolbar|Content) needs to run in the same
		// component that owns useRichTextEditorIntermediateState. See #42384
		const answerToolbarProps: IResolveAttachableRichTextEditorPropsWithBuilder<string> = answerHandler
			.attachToRichTextEditorToolbar(answerIntermediateState);
		const answerContentProps: IResolveAttachableRichTextEditorPropsWithBuilder<string> = answerHandler
			.attachToRichTextEditorContent(answerIntermediateState);

		const hasError: boolean = attachable
			.validation
			.getValidationState()
			.kind === InputValidationResultKind.Invalid;

		useImperativeHandle(
			ref,
			(): ItemHandle => ({
				focusQuestion: (): void => {
					questionRef.current
						?.focus();
				},
				scrollIntoView: () => {
					itemRef.current
						?.scrollIntoView();
				}
			}),
			[]
		);

		const closeImproveTextMenuHandler = useCallback(
			(): void => {
				setIsImproveTextMenuOpen(false);
			},
			[]
		);

		// We need a delay because clicking an item inside the
		// improve text menu fires blur before focusing the element
		const [blurHandler, cancelBlurHandler] = useDelayedEvent(
			(): void => {
				setIsActive(false);
				setActiveField(null);
				closeImproveTextMenuHandler();
			},
			0
		);

		const focusQuestionHandler = useCallback(
			(): void => {
				questionRef.current
					?.focus();
			},
			[]
		);

		const focusAnswerHandler = useCallback(
			(): void => {
				answerRef.current
					?.focus();
			},
			[]
		);

		const changeQuestionHandler = useCallback(
			(newValue: string): void => {
				questionHandler.reducer
					.setValue(newValue);
			},
			[questionHandler.reducer]
		);

		const questionSelectionRewriteResult: RewriteToolsResult<string, string> = usePlainTextRewriteTools(
			questionHandler.reducer.value,
			changeQuestionHandler,
			focusQuestionHandler,
			{
				kind: "specific-range",
				from: questionSelectionRange[0],
				to: questionSelectionRange[1],
				shouldUseEntireContentWhenEmpty: false
			},
			useRewriteToolsService(
				hubItemKindToRewriteToolTargetKindMap[hubItemKind],
				RewriteToolEditorKind.PlainText
			)
		);

		const answerSelectionRewriteResult: RichTextEditorRewriteToolsResult = useRichTextEditorRewriteTools(
			answerIntermediateState.intermediateState,
			answerIntermediateState.setIntermediateState,
			focusAnswerHandler,
			{
				kind: "selection",
				shouldUseEntireDocumentWhenEmpty: false
			},
			useRewriteToolsService(
				hubItemKindToRewriteToolTargetKindMap[hubItemKind],
				RewriteToolEditorKind.RichText
			)
		);

		const entireQuestionRewriteResult: RewriteToolsResult<string, string> = usePlainTextRewriteTools(
			questionHandler.reducer.value,
			changeQuestionHandler,
			focusQuestionHandler,
			{ kind: "entire-content" },
			useRewriteToolsService(
				hubItemKindToRewriteToolTargetKindMap[hubItemKind],
				RewriteToolEditorKind.PlainText
			)
		);

		const entireAnswerRewriteResult: RichTextEditorRewriteToolsResult = useRichTextEditorRewriteTools(
			answerIntermediateState.intermediateState,
			answerIntermediateState.setIntermediateState,
			focusAnswerHandler,
			{ kind: "entire-document" },
			useRewriteToolsService(
				hubItemKindToRewriteToolTargetKindMap[hubItemKind],
				RewriteToolEditorKind.RichText
			)
		);

		const itemRewriteResult = useMemo(
			(): ItemRewriteToolsResult => {
				return mergeQuestionAndAnswerResults(
					entireQuestionRewriteResult,
					entireAnswerRewriteResult
				);
			},
			[entireAnswerRewriteResult, entireQuestionRewriteResult]
		);

		const activeRewriteToolsResult = useMemo(
			(): RewriteToolsResult<unknown, unknown, unknown> => {
				if (activeField === "question" && questionSelectionRewriteResult.canRewrite) {
					return questionSelectionRewriteResult as RewriteToolsResult<unknown, unknown, unknown>;
				}

				if (activeField === "answer" && answerSelectionRewriteResult.canRewrite) {
					return answerSelectionRewriteResult as RewriteToolsResult<unknown, unknown, unknown>;
				}

				return itemRewriteResult as RewriteToolsResult<unknown, unknown, unknown>;
			},
			[
				activeField,
				answerSelectionRewriteResult,
				itemRewriteResult,
				questionSelectionRewriteResult
			]
		);

		const deleteHandler = useCallback(
			() => {
				clearFocus();
				onDelete();
			},
			[onDelete]
		);

		const trackingId: string = attachable
			.reducer
			.path;

		return (
			<li
				ref={itemRef}
				tabIndex={0}
				className={classNames(
					styles.item,
					{
						[styles.item_active]: isActive,
						[styles.item_hasError]: hasError
					}
				)}
				onClick={e => {
					e.stopPropagation();
				}}
				onFocus={e => {
					e.stopPropagation();
					cancelBlurHandler();
					setIsActive(true);
				}}
				onBlur={blurHandler}
			>
				{isActive && (
					<ElementLayout.ActionsMenu
						trackingId="faq-actions"
						visibilityPreference="always"
					>
						{onMoveUp && (
							<ElementLayout.MoveUpMenuItem
								onClick={onMoveUp}
							/>
						)}
						{onMoveDown && (
							<ElementLayout.MoveDownMenuItem
								onClick={onMoveDown}
							/>
						)}
						<ElementLayout.DeleteMenuItem
							onClick={deleteHandler}
						/>
						<InlineActionList.Divider />
						<Button
							kind="transparent-for-list"
							icon="plus"
							text={localize("_AddQuestion")}
							onClick={onAdd}
							trackingId="add-question"
						/>
						{isAssistMeAvailable
							&& !isTouchDevice
							&& activeRewriteToolsResult.canRewrite && (
								<>
									<InlineActionList.Divider />
									<ElementLayout.ImproveTextMenuItem
										onClick={() => setIsImproveTextMenuOpen(true)}
										ref={improveTextButtonRef}
									/>
									{isImproveTextMenuOpen
										&& (
											<RewriteToolsMenu
												handlers={activeRewriteToolsResult.handlers}
												onClose={closeImproveTextMenuHandler}
												target={ensureNotNil(improveTextButtonRef.current)}
												trackingId="improve-text__drop-over"
											/>
										)
									}
								</>
							)
						}
						{onSelectEntireFAQ && (
							<>
								<InlineActionList.Divider />
								<Button
									kind="transparent-for-list"
									icon="circle-check"
									text={localize("_SelectEntireFAQ")}
									onClick={onSelectEntireFAQ}
									trackingId="select-entire-faq"
								/>
							</>
						)}
					</ElementLayout.ActionsMenu>
				)}
				<FrequentlyAskedQuestionsItemLayout trackingId={trackingId}>
					<FrequentlyAskedQuestionsItemLayout.Question trackingId="question">
						<Question
							ref={questionRef}
							attachable={questionHandler}
							onFocus={() => setActiveField("question")}
							onSelectionChange={(from, to) => setQuestionSelectionRange([from, to])}
						/>
					</FrequentlyAskedQuestionsItemLayout.Question>
					<FrequentlyAskedQuestionsItemLayout.Answer
						trackingId="answer"
						isExpanded
					>
						<Answer
							ref={answerRef}
							attachable={answerHandler}
							contentProps={answerContentProps}
							toolbarProps={answerToolbarProps}
							onFocus={() => setActiveField("answer")}
						/>
					</FrequentlyAskedQuestionsItemLayout.Answer>
				</FrequentlyAskedQuestionsItemLayout>
			</li>
		);
	}
);
Item.displayName = "Item";

type QuestionHandle = {
	focus: () => void;
};

type QuestionProps = {
	attachable: IValueInputAttachable<string>;
	onFocus: () => void;
	onSelectionChange: (from: number, to: number) => void;
};

const Question = forwardRef(
	(props: QuestionProps, ref: Ref<QuestionHandle>): ReactElement => {
		const {
			attachable,
			onFocus,
			onSelectionChange
		} = props;

		const {
			activate
		} = useBuilderEditor();

		const textAreaRef: RefObject<AutogrowTextAreaHandle | null> = useRef<AutogrowTextAreaHandle>(null);

		useImperativeHandle(
			ref,
			(): QuestionHandle => ({
				focus: (): void => {
					textAreaRef.current
						?.focus();
				}
			}),
			[]
		);

		return (
			<>
				<h4>
					<AutogrowTextArea
						{...attachable.attachToTextArea()}
						ref={textAreaRef}
						placeholder={localize("_EnterQuestion")}
						hasNoPadding
						isBorderless
						hasNoMinHeight
						onFocus={() => {
							activate();
							onFocus();
						}}
						hasNoBackgroundColor
						onSelectionChange={onSelectionChange}
					/>
				</h4>
				<ValidationError {...attachable.attachToValidationError()} />
			</>
		);
	}
);
Question.displayName = "Question";

type AnswerHandle = {
	focus: () => void;
};

type AnswerProps = {
	attachable: IValueInputAttachable<string>;
	contentProps: IResolveAttachableRichTextEditorPropsWithBuilder<string>;
	toolbarProps: IResolveAttachableRichTextEditorPropsWithBuilder<string>;
	onFocus: () => void;
	ref: Ref<AnswerHandle>;
};

const Answer = (props: AnswerProps): ReactElement => {
	const {
		attachable,
		contentProps,
		toolbarProps,
		onFocus,
		ref
	} = props;

	const contentRef: RefObject<RichTextEditorContentHandle | null> = useRef<RichTextEditorContentHandle>(null);
	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const getHasChangesHandler = useCallback(
		(): boolean => {
			const content: RichTextEditorContentHandle | null = contentRef.current;
			return isNotNil(content) && content.getUndoDepth() > 0;
		},
		[]
	);

	const {
		activate,
		isActive,
		toolbarContainer
	} = useBuilderEditor({
		getHasChanges: getHasChangesHandler
	});

	const focusInputHandler = useCallback(
		(): void => {
			contentRef.current?.focus();
		},
		[]
	);

	const findUsersHandler = useCallback(
		(
			searchTerm: string,
			amount: number,
			signal: AbortSignal
		): Promise<readonly UserIdentityModel[]> => {
			return findUsers(
				searchTerm,
				amount,
				signal
			);
		},
		[]
	);

	useImperativeHandle(
		ref,
		(): AnswerHandle => ({
			focus: (): void => {
				contentRef.current
					?.focus();
			}
		}),
		[]
	);

	return (
		<div ref={containerRef}>
			{isActive && isNotNil(toolbarContainer) && (
				<Portal container={toolbarContainer}>
					<RichTextEditorToolbar
						{...toolbarProps}
						onFocusInput={focusInputHandler}
						isBorderless
						hasNoPadding
						areHashUrlsAllowed
					/>
				</Portal>
			)}
			<RichTextEditorContent
				{...contentProps}
				placeholder={localize("_EnterAnswer")}
				onFocus={() => {
					activate();
					onFocus();
				}}
				ref={contentRef}
				height="auto"
				isBorderless
				hasNoPadding
				hasNoBackgroundColor
				mentionTypes={{
					[userMentionType]: {
						kind: "insert-anchor",
						hrefPattern: generateUserDetailsUrl
					}
				}}
				renderSuggestionBox={
					(props, ref, rect) => (
						<DropOverMentionSuggestionBox
							{...props}
							ref={ref}
							target={ensureNotNil(containerRef.current)}
							findUsers={findUsersHandler}
							rect={rect}
						/>
					)
				}
			/>
			<ValidationError {...attachable.attachToValidationError()} />
		</div>
	);
};
Answer.displayName = "Answer";

type ItemSlice = {
	questionPart: string | undefined;
	answerPart: Slice | undefined;
};

type ItemContent = {
	questionPart: string | undefined;
	answerPart: EditorState | undefined;
};

type ItemPosition = {
	questionPart: number | undefined;
	answerPart: number | undefined;
};

type ItemRewriteToolsResult = RewriteToolsResult<ItemSlice, ItemContent, ItemPosition>;
type ItemRewriteToolHandlers = RewriteToolHandlers<ItemSlice, ItemContent, ItemPosition>;

function mergeQuestionAndAnswerResults(
	questionResult: RewriteToolsResult<string, string>,
	answerResult: RichTextEditorRewriteToolsResult
): ItemRewriteToolsResult {
	if (!questionResult.canRewrite && !answerResult.canRewrite) {
		return {
			canRewrite: false
		};
	}

	return {
		canRewrite: true,
		handlers: ensureNotNil(
			mergeQuestionAndAnswerHandlers(
				questionResult.handlers,
				answerResult.handlers
			)
		)
	};
}

function mergeQuestionAndAnswerHandlers(
	questionHandlers: RewriteToolHandlers<string, string> | undefined,
	answerHandlers: RichTextEditorRewriteToolHandlers | undefined
): ItemRewriteToolHandlers | undefined {
	if (!questionHandlers && !answerHandlers) {
		return undefined;
	}

	return {
		apply: (response: SuccessfulRewriteToolResponse<ItemSlice, ItemContent, ItemPosition>) => {
			if (questionHandlers) {
				questionHandlers.apply({
					status: "success",
					updatedSlice: ensureNotNil(
						response.updatedSlice
							.questionPart
					),
					originalSliceInfo: extractOriginalSliceInfoForQuestion(response.originalSliceInfo)
				});
			}

			if (answerHandlers) {
				answerHandlers.apply({
					status: "success",
					updatedSlice: ensureNotNil(
						response.updatedSlice
							.answerPart
					),
					originalSliceInfo: extractOriginalSliceInfoForAnswer(response.originalSliceInfo)
				});
			}
		},
		changeTone: (tone: RewriteToneKind, signal?: AbortSignal): Promise<RewriteToolResponse<ItemSlice, ItemContent, ItemPosition>> => {
			return mergeResponses(
				questionHandlers?.changeTone(tone, signal),
				answerHandlers?.changeTone(tone, signal)
			);
		},
		fixSpelling: (signal?: AbortSignal): Promise<RewriteToolResponse<ItemSlice, ItemContent, ItemPosition>> => {
			return mergeResponses(
				questionHandlers?.fixSpelling(signal),
				answerHandlers?.fixSpelling(signal)
			);
		},
		makeShorter: (signal?: AbortSignal): Promise<RewriteToolResponse<ItemSlice, ItemContent, ItemPosition>> => {
			return mergeResponses(
				questionHandlers?.makeShorter(signal),
				answerHandlers?.makeShorter(signal)
			);
		},
		makeLonger: (signal?: AbortSignal): Promise<RewriteToolResponse<ItemSlice, ItemContent, ItemPosition>> => {
			return mergeResponses(
				questionHandlers?.makeLonger(signal),
				answerHandlers?.makeLonger(signal)
			);
		},
		translate: (lang: TranslateLangKind, signal?: AbortSignal): Promise<RewriteToolResponse<ItemSlice, ItemContent, ItemPosition>> => {
			return mergeResponses(
				questionHandlers?.translate(lang, signal),
				answerHandlers?.translate(lang, signal)
			);
		},
		maxContentLength: ensureNotNil(
			questionHandlers?.maxContentLength
				?? answerHandlers?.maxContentLength
		),
		getPreviewHtml: (response: SuccessfulRewriteToolResponse<ItemSlice, ItemContent, ItemPosition>): string => {
			const questionHtml: string = isNotNil(questionHandlers)
				? questionHandlers.getPreviewHtml({
					status: "success",
					updatedSlice: ensureNotNil(
						response.updatedSlice
							.questionPart
					),
					originalSliceInfo: extractOriginalSliceInfoForQuestion(response.originalSliceInfo)
				})
				: "";

			const answerHtml: string = isNotNil(answerHandlers)
				? answerHandlers.getPreviewHtml({
					status: "success",
					updatedSlice: ensureNotNil(
						response.updatedSlice
							.answerPart
					),
					originalSliceInfo: extractOriginalSliceInfoForAnswer(response.originalSliceInfo)
				})
				: "";

			return questionHtml + answerHtml;
		}
	};
}

async function mergeResponses(
	questionPromise: Promise<RewriteToolResponse<string, string, number>> | undefined,
	answerPromise: Promise<RewriteToolResponse<Slice, EditorState, number>> | undefined
): Promise<RewriteToolResponse<ItemSlice, ItemContent, ItemPosition>> {
	const [questionResult, answerResult] = await Promise.all([
		questionPromise,
		answerPromise
	]);

	const originalSliceInfo: RewriteToolOriginalSliceInfo<ItemContent, ItemPosition> = {
		entireContent: {
			questionPart: questionResult?.originalSliceInfo
				.entireContent,
			answerPart: answerResult?.originalSliceInfo
				.entireContent
		},
		from: {
			questionPart: questionResult?.originalSliceInfo
				.from,
			answerPart: answerResult?.originalSliceInfo
				.from
		},
		to: {
			questionPart: questionResult?.originalSliceInfo
				.to,
			answerPart: answerResult?.originalSliceInfo
				.to
		}
	};

	if (questionResult && questionResult.status === "content-too-large") {
		return {
			status: "content-too-large",
			originalSliceInfo
		};
	}

	if (answerResult && answerResult.status === "content-too-large") {
		return {
			status: "content-too-large",
			originalSliceInfo
		};
	}

	return {
		status: "success",
		originalSliceInfo,
		updatedSlice: {
			questionPart: questionResult?.updatedSlice,
			answerPart: answerResult?.updatedSlice
		}
	};
}

function extractOriginalSliceInfoForQuestion(
	originalSliceInfo: RewriteToolOriginalSliceInfo<ItemContent, ItemPosition>
): RewriteToolOriginalSliceInfo<string, number> {
	return {
		from: ensureNotNil(
			originalSliceInfo
				.from
				.questionPart
		),
		to: ensureNotNil(
			originalSliceInfo
				.to
				.questionPart
		),
		entireContent: ensureNotNil(
			originalSliceInfo
				.entireContent
				.questionPart
		)
	};
}

function extractOriginalSliceInfoForAnswer(
	originalSliceInfo: RewriteToolOriginalSliceInfo<ItemContent, ItemPosition>
): RewriteToolOriginalSliceInfo<EditorState, number> {
	return {
		from: ensureNotNil(
			originalSliceInfo
				.from
				.answerPart
		),
		to: ensureNotNil(
			originalSliceInfo
				.to
				.answerPart
		),
		entireContent: ensureNotNil(
			originalSliceInfo
				.entireContent
				.answerPart
		)
	};
}